import { Component, inject, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../shared/loading-indicator/loading-indicator';
import { ISchool, ISchools } from '../../interfaces/school';
import { SchoolService } from '../../services/school';
import { ApiEndpoints } from '../../core/api-endpoints';
import { AuthService } from '../../services/auth';
import { Router } from '@angular/router';
import { Session } from '../../services/session';
import { FlashService } from '../../services/flash';
import { UserService } from '../../services/user';
import { IUsers } from '../../interfaces/user';

@Component({
  selector: 'app-signup',
  imports: [FormsModule, LoadingIndicator],
  templateUrl: './signup.html',
  styleUrl: './signup.css',
})
export class Signup {
  constructor(
    private schoolService: SchoolService,
    private authService: AuthService,
    private router: Router,
    private session: Session,
    protected userService: UserService,
  ) {
    const token = this.session.getItem('token');

    if (this.session.decodeToken(token)?.role !== 'SUPER_ADMIN' || !token) {
      this.router.navigate(['/']);
      return;
    }
  }

  protected firstName: string = '';
  protected lastName: string = '';
  protected username: string = '';
  protected temporaryPassword: string = '';
  protected role: string = '';
  protected query: string = '';
  protected schoolId: string = '';
  protected schoolPublicId!: string;
  protected schoolName: string = '';
  protected isLoading = signal<boolean>(false);
  protected showPassword: boolean = false;
  protected schools = signal<ISchools | null>(null);
  protected readonly flashService = inject(FlashService);
  protected users = signal<IUsers | null>(null);

  protected selectSchool(school: ISchool['payload']) {
    this.query = '';
    this.schoolId = school.id;
    this.schoolName = school.school_name;
    this.schoolPublicId = school.public_id;
  }

  protected getUsers(key: string): void {
    this.userService
      .getUserByUsername(`${ApiEndpoints.url}/user/username?school_id=${this.schoolId}&username=${key}`)
      .subscribe({
        next: (data) => {
          this.users.set(data);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Cannot verify username');
        },
      });
  }

  protected getSchools(value: string): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school?search=${value}`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected signup(form: NgForm): void {
    this.isLoading.set(true);

    const username = this.username.toLowerCase();

    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    if (this.temporaryPassword.length < 8) {
      this.flashService.failure('Password must be at least 8 characters long');
      return;
    }

    const signupForm = {
      first_name: this.firstName.trim(),
      last_name: this.lastName.trim(),
      username: username.trim(),
      password: this.temporaryPassword.trim(),
      role: this.role,
      school_id: this.schoolId,
    };

    this.authService.signup(`${ApiEndpoints.url}/auth/signup`, signupForm).subscribe({
      next: (data) => {
        this.isLoading.set(false);
        this.flashService.success('User created successfully.');
      },
      error: (err) => {
        this.isLoading.set(false);
        this.flashService.failure(err?.error?.message ?? 'User creation failed');
      },
    });
  }
}
